"use client";

import { c as _c } from "react/compiler-runtime";
import { FloatingFocusManager, FloatingPortal, flip, offset, safePolygon, shift, useClick, useDismiss, useFloating, useHover, useInteractions, useRole } from "@floating-ui/react";
import clsx from "clsx";
import { FC, PropsWithChildren, useState } from "react";
import { PopoverProps } from "./types";
const Popover: FC<PropsWithChildren<PopoverProps>> = t0 => {
  const $ = _c(37);
  const {
    children,
    content,
    variant: t1,
    trigger: t2,
    padding: t3,
    isOpen,
    dismissOnOutsidePress: t4,
    placement,
    onOpenChange
  } = t0;
  const variant = t1 === undefined ? "primary" : t1;
  const trigger = t2 === undefined ? "click" : t2;
  const padding = t3 === undefined ? true : t3;
  const dismissOnOutsidePress = t4 === undefined ? true : t4;
  const isControlled = typeof isOpen === "boolean";
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const internalIsOpen = isControlled ? isOpen : isPopoverOpen;
  const handleOpenChange = isControlled ? onOpenChange : setIsPopoverOpen;
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [offset(8), flip({
      padding: 8
    }), shift({
      padding: 8
    })];
    $[0] = t5;
  } else {
    t5 = $[0];
  }
  let t6;
  if ($[1] !== handleOpenChange || $[2] !== internalIsOpen || $[3] !== placement) {
    t6 = {
      placement,
      open: internalIsOpen,
      onOpenChange: handleOpenChange,
      middleware: t5
    };
    $[1] = handleOpenChange;
    $[2] = internalIsOpen;
    $[3] = placement;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const {
    refs: t7,
    floatingStyles,
    context
  } = useFloating(t6);
  const {
    setReference,
    setFloating
  } = t7;
  const t8 = trigger !== "click";
  let t9;
  if ($[5] !== t8) {
    t9 = {
      ignoreMouse: t8,
      keyboardHandlers: true
    };
    $[5] = t8;
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  const click = useClick(context, t9);
  const t10 = trigger === "hover";
  let t11;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = safePolygon();
    $[7] = t11;
  } else {
    t11 = $[7];
  }
  let t12;
  if ($[8] !== t10) {
    t12 = {
      enabled: t10,
      handleClose: t11
    };
    $[8] = t10;
    $[9] = t12;
  } else {
    t12 = $[9];
  }
  const hover = useHover(context, t12);
  const role = useRole(context);
  let t13;
  if ($[10] !== dismissOnOutsidePress) {
    t13 = {
      outsidePress: dismissOnOutsidePress
    };
    $[10] = dismissOnOutsidePress;
    $[11] = t13;
  } else {
    t13 = $[11];
  }
  const dismiss = useDismiss(context, t13);
  let t14;
  if ($[12] !== click || $[13] !== dismiss || $[14] !== hover || $[15] !== role) {
    t14 = [click, role, hover, dismiss];
    $[12] = click;
    $[13] = dismiss;
    $[14] = hover;
    $[15] = role;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  const {
    getReferenceProps,
    getFloatingProps
  } = useInteractions(t14);
  let t15;
  let t16;
  if ($[17] !== content || $[18] !== context || $[19] !== floatingStyles || $[20] !== getFloatingProps || $[21] !== internalIsOpen || $[22] !== padding || $[23] !== setFloating || $[24] !== variant) {
    const variants = {
      primary: "bg-primary-800 text-neutral-0 shadow-3",
      white: "bg-neutral-0 text-neutral-675 shadow-3"
    };
    t15 = "block h-fit w-fit";
    t16 = internalIsOpen && <FloatingPortal><FloatingFocusManager context={context} modal={false}><div style={floatingStyles} ref={setFloating} className={clsx("w-max rounded-s max-w-[calc(100%-16px)] m:max-w-[360px] focus:outline-none z-[52] text-body-2", variants[variant], {
          "px-2s py-3xs": padding
        })} {...getFloatingProps()}>{content}</div></FloatingFocusManager></FloatingPortal>;
    $[17] = content;
    $[18] = context;
    $[19] = floatingStyles;
    $[20] = getFloatingProps;
    $[21] = internalIsOpen;
    $[22] = padding;
    $[23] = setFloating;
    $[24] = variant;
    $[25] = t15;
    $[26] = t16;
  } else {
    t15 = $[25];
    t16 = $[26];
  }
  let t17;
  if ($[27] !== getReferenceProps) {
    t17 = getReferenceProps();
    $[27] = getReferenceProps;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== children || $[30] !== setReference || $[31] !== t17) {
    t18 = <div tabIndex={0} role="button" ref={setReference} {...t17} className="h-fit w-fit block">{children}</div>;
    $[29] = children;
    $[30] = setReference;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t15 || $[34] !== t16 || $[35] !== t18) {
    t19 = <span className={t15}>{t16}{t18}</span>;
    $[33] = t15;
    $[34] = t16;
    $[35] = t18;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  return t19;
};
export default Popover;